import React, { useMemo, useState } from "react";
import { Box, Divider, ListItemIcon, MenuItem, MenuList } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { DotLegalFullScreenSpinner, DotLegalHeader, DotLegalPrettyScrollbar, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { FilterOption } from "../FilterPopover.types.ts";
import { useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { GetListData } from "./SubMenuDataHelper.ts";
import { useTranslation } from "@localization/useTranslation.ts";
import { isNullOrWhitespace } from "@common/stringOperations.ts";
import { StyledTextField } from "../FilterPopover.tsx";
import { EntityType } from "@common/Common.types.ts";
import { getEntityTypeIcon } from "@common/components/entityTypeIcon/entityTypeIcon.tsx";
import { IdNameWithEntityType } from "../../filterBar/IdNameWithEntityType.ts";
import FilterPopoverSubMenuEmptyState from "../FilterPopoverSubMenuEmptyState.tsx";

interface TaskRelatedEntityResponse {
    entityId: string;
    entityType: EntityType;
    entityName: string;
}

export interface RelatedEntitySubMenuProps {
    selectedOption: FilterOption;
    onToggleSelect: (ids: Array<string>, options: Array<IdNameWithEntityType>) => void;
    submenuInputRef: React.RefObject<HTMLInputElement>;
    filterBarData?: Array<IdNameWithEntityType>;
}

function RelatedEntitySubMenu(props: RelatedEntitySubMenuProps) {
    const { selectedOption, onToggleSelect, submenuInputRef, filterBarData } = props;
    const { translateString, translateEntityType } = useTranslation();
    const [search, setSearch] = useState("");

    const { isLoading, data } = useDotLegalQueryWithKey<Array<TaskRelatedEntityResponse>>(
        selectedOption.dataUrl ?? "task-related-entities",
        selectedOption.dataUrl!,
        { enabled: !isNullOrWhitespace(selectedOption.dataUrl), staleTime: 15000 }
    );

    const chosenIds = GetListData(selectedOption.value);

    const options = useMemo<Array<IdNameWithEntityType>>(
        () => (data ?? []).map((d) => ({ id: d.entityId, name: d.entityName, entityType: d.entityType })),
        [data]
    );

    // Already-selected rows, surfaced as a flat list above the search input for quick deselection.
    // Prefer the picker's loaded options (fresh entity-type info) and fall back to filterBarData
    // (chip-resolution payload from /filter-data) so the chosen list still renders before the
    // picker endpoint has resolved.
    const chosenOptions = useMemo<Array<IdNameWithEntityType>>(() => {
        if (chosenIds.length === 0) return [];
        const byId = new Map<string, IdNameWithEntityType>();
        for (const o of options) {
            if (chosenIds.includes(o.id)) byId.set(o.id, o);
        }
        for (const o of filterBarData ?? []) {
            if (chosenIds.includes(o.id) && !byId.has(o.id)) byId.set(o.id, o);
        }
        return chosenIds.map((id) => byId.get(id)).filter((x): x is IdNameWithEntityType => x !== undefined);
    }, [chosenIds, options, filterBarData]);

    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase();
        if (!needle) return options;
        return options.filter((o) => o.name.toLowerCase().includes(needle));
    }, [options, search]);

    const grouped = useMemo(() => {
        const groups = new Map<EntityType, Array<IdNameWithEntityType>>();
        for (const o of filtered) {
            const existing = groups.get(o.entityType);
            if (existing) existing.push(o);
            else groups.set(o.entityType, [o]);
        }
        return Array.from(groups.entries()).sort(([a], [b]) => a - b);
    }, [filtered]);

    const toggle = (option: IdNameWithEntityType) => {
        if (chosenIds.includes(option.id)) {
            const newIds = chosenIds.filter((x) => x !== option.id);
            const newData = (filterBarData ?? []).filter((x) => x.id !== option.id);
            onToggleSelect(newIds, newData);
        } else {
            const newIds = [...chosenIds, option.id];
            const newData = [...(filterBarData ?? []), option];
            onToggleSelect(newIds, newData);
        }
    };

    const renderChosenRow = (item: IdNameWithEntityType) => (
        <MenuItem
            key={`chosen-${item.id}`}
            onClick={() => toggle(item)}
            sx={(theme) => ({
                paddingX: 2,
                whiteSpace: "wrap",
                fontSize: theme.typography.pxToRem(14),
                "& .MuiListItemIcon-root": { minWidth: 24 },
                "&:hover": {
                    backgroundColor: theme.palette.grey[100],
                },
            })}
        >
            <ListItemIcon sx={{ minWidth: 24 }}>
                <CheckIcon fontSize="small" sx={{ fontSize: "16px" }} />
            </ListItemIcon>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 0, flex: 1 }}>
                <DotLegalTooltip text={translateEntityType(EntityType[item.entityType])}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            "& svg": { width: 16, height: 16, fill: "#DAD9EF" },
                        }}
                    >
                        {getEntityTypeIcon(item.entityType)}
                    </Box>
                </DotLegalTooltip>
                <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</Box>
            </Box>
        </MenuItem>
    );

    return (
        <Box>
            {chosenOptions.length > 0 && (
                <>
                    <MenuList sx={{ p: 0 }}>{chosenOptions.map(renderChosenRow)}</MenuList>
                    <Divider />
                </>
            )}
            <StyledTextField
                inputRef={submenuInputRef}
                fullWidth
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`${translateString("filter")}...`}
                slotProps={{ input: { disableUnderline: true } }}
                variant={"standard"}
                autoComplete="off"
            />
            <Divider />
            {isLoading ? (
                <Box sx={{ p: "24px" }}>
                    <DotLegalFullScreenSpinner />
                </Box>
            ) : (
                <DotLegalPrettyScrollbar>
                    <MenuList sx={{ p: 0, maxHeight: 450 }}>
                        {grouped.length === 0 ? (
                            <FilterPopoverSubMenuEmptyState emptyText={translateString("noFilterResultsText")} />
                        ) : (
                            grouped.map(([entityType, items]) => (
                                <React.Fragment key={entityType}>
                                    <Box sx={{ px: 2 }}>
                                        <DotLegalHeader headerStyle="small" marginTop={1} marginBottom={1}>
                                            {translateEntityType(EntityType[entityType])}
                                        </DotLegalHeader>
                                    </Box>
                                    {items.map((item) => {
                                        const isSelected = chosenIds.includes(item.id);
                                        return (
                                            <MenuItem
                                                key={item.id}
                                                onClick={() => toggle(item)}
                                                sx={(theme) => ({
                                                    paddingX: 2,
                                                    whiteSpace: "wrap",
                                                    fontSize: theme.typography.pxToRem(14),
                                                    "& .MuiListItemIcon-root": { minWidth: 24 },
                                                    "& .related-entity-check": {
                                                        visibility: isSelected ? "visible" : "hidden",
                                                        opacity: isSelected ? 1 : 0,
                                                    },
                                                    "&:hover": {
                                                        backgroundColor: theme.palette.grey[100],
                                                        "& .related-entity-check": {
                                                            visibility: "visible",
                                                            opacity: isSelected ? 1 : 0.4,
                                                        },
                                                    },
                                                })}
                                            >
                                                <ListItemIcon className="related-entity-check" sx={{ minWidth: 24 }}>
                                                    <CheckIcon fontSize="small" sx={{ fontSize: "16px" }} />
                                                </ListItemIcon>
                                                <Box sx={{ display: "flex", gap: 1, alignItems: "center", minWidth: 0, flex: 1 }}>
                                                    <DotLegalTooltip text={translateEntityType(EntityType[item.entityType])}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                "& svg": { width: 16, height: 16, fill: "#DAD9EF" },
                                                            }}
                                                        >
                                                            {getEntityTypeIcon(item.entityType)}
                                                        </Box>
                                                    </DotLegalTooltip>
                                                    <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</Box>
                                                </Box>
                                            </MenuItem>
                                        );
                                    })}
                                </React.Fragment>
                            ))
                        )}
                    </MenuList>
                </DotLegalPrettyScrollbar>
            )}
        </Box>
    );
}

export default RelatedEntitySubMenu;
